/* ==========================================================================
   Element side cart — theme-native slide cart.

   Design notes worth keeping in mind before editing:

   1. NO MutationObserver anywhere. An observer that writes inside its own
      observed subtree hangs the main thread and shows up as a permanent
      logo spinner. Cart-icon binding is a delegated click on `document`,
      which also covers icons injected after load.

   2. Init does NOT wait on DOMContentLoaded. On preview themes that event
      can never fire, which leaves every listener dormant and looks exactly
      like broken code. We run as soon as the script executes.

   3. The checkout button is SERVER-RENDERED by the section inside
      `.gokwik-checkout` and is never replaced by a re-render. That is the
      selector snippets/gokwik.liquid enables and GoKwik's merchant
      integration binds, so checkout keeps working untouched.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.ElementCartConfig || {};
  var MONEY = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  var MONEY0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

  var els = {};
  var productCache = {};
  var cart = null;
  var tierPage = 0;
  var pickerFor = null;   /* tier index whose gift picker is open */
  var unlockedSeen = {};
  var busy = false;
  var booted = false;

  /* ---------- helpers ---------- */

  function money(paise) { return '₹' + MONEY.format(paise / 100); }
  function moneyFlat(paise) { return '₹' + MONEY0.format(Math.ceil(paise / 100)); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function pctOff(mrp, price) {
    return mrp > price ? Math.round((mrp - price) / mrp * 100) : 0;
  }

  function tiers() {
    return (CFG.giftTiers || []).filter(function (t) { return t && t.amount > 0; });
  }

  /* Gift lines are tagged with an underscore-prefixed line property, which
     Shopify hides from the customer and from the order confirmation body
     but keeps queryable on the order. That tag is the ONLY way we recognise
     a line we added ourselves, so never rename it casually. */
  var GIFT_PROP = '_free_gift';

  /* Resolved once from /products/<handle>.js — see the section comment for
     why the pool is not built in Liquid. */
  var poolCache = null;

  function giftPool() { return poolCache || []; }

  /* Each tier can offer its own shortlist (tier_N_gifts). A tier with no list
     of its own falls back to the shared pool, so this stays a one-list setup
     unless the merchant actually wants different gifts per tier. */
  function poolForTier(i) {
    var per = ((CFG.tierGiftHandles || [])[i] || []).filter(Boolean);
    if (!per.length) return giftPool();
    var out = [];
    per.forEach(function (h) {
      for (var j = 0; j < (poolCache || []).length; j++) {
        if (poolCache[j].handle === h) { out.push(poolCache[j]); return; }
      }
    });
    return out;
  }

  /* Gift titles are full catalogue names ("Brightening Face Wash - 3%
     Niacinamide with Vitamin C and Rice Water - Pack of 1"), far too long for
     a half-width tier card. Trim at the first spec clause; the untouched name
     is still on the cart line and on the picker tile's tooltip. */
  /* ONE naming authority for every product name the drawer prints — line
     items, the gift ladder, the gift sheet and the upsell rail. Prefers the
     store's curated custom.short_name (same string the product cards and the
     PDP sticky bar use) and only derives a name when the map has no entry.
     The untrimmed title is always kept for the title="" tooltip. */
  var SHORT = CFG.shortNames || {};

  function displayName(handle, fullTitle) {
    return (handle && SHORT[handle]) || shortGiftName(fullTitle);
  }

  function shortGiftName(t) {
    var full = String(t || '').replace(/\uD83C\uDF81/g, '').trim();
    var out = full;
    ['  -  ', ' - ', ' \u2013 ', ' with '].forEach(function (sep) {
      var head = out.split(sep)[0].trim();
      if (head.length >= 12) out = head;
    });
    return out || full;
  }

  function resolveGiftPool() {
    if (poolCache) return Promise.resolve(poolCache);
    var handles = (CFG.giftHandles || []).filter(Boolean);
    /* Resolve every handle any tier can offer, not just the shared list. */
    (CFG.tierGiftHandles || []).forEach(function (list) {
      (list || []).filter(Boolean).forEach(function (h) {
        if (handles.indexOf(h) === -1) handles.push(h);
      });
    });
    if (!handles.length) { poolCache = []; return Promise.resolve(poolCache); }
    return Promise.all(handles.map(getProduct)).then(function (ps) {
      poolCache = [];
      ps.forEach(function (p) {
        if (!p || !p.variants) return;
        /* <= Rs.1, not === 0: the Lip Balm gift SKU is priced at 1 paise, so an
           exact-zero test silently dropped it from the pool. Still tight enough
           that a real product can never be carted free by mistake. */
        var v = null;
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].available && p.variants[i].price <= 100) { v = p.variants[i]; break; }
        }
        if (!v) { console.warn('[element-cart] no free variant for gift', p.handle); return; }
        poolCache.push({
          handle: p.handle,
          variantId: v.id,
          productId: p.id,
          title: displayName(p.handle, p.title),
          fullTitle: String(p.title).replace(/\uD83C\uDF81/g, '').trim(),
          rawTitle: String(p.title).trim(),
          variantTitle: v.title && v.title !== 'Default Title' ? v.title : '',
          mrp: v.compare_at_price || 0,
          image: p.featured_image || (p.images && p.images[0]) || ''
        });
      });
      return poolCache;
    });
  }

  function poolByVariant(vid) {
    var p = giftPool();
    for (var i = 0; i < p.length; i++) if (p[i].variantId === vid) return p[i];
    return null;
  }

  /* Which gift line, if any, is currently filling a given tier. Derived from
     the cart rather than kept in local state, so a second tab or a browser
     back button can never disagree with what is actually in the cart. */
  function chosenFor(tierName) {
    if (!cart) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGift(it) && it.properties[GIFT_PROP] === tierName) return it;
    }
    return null;
  }

  /* Two cards fit at a time, so the last valid page is (tiers - 2). With the
     usual two tiers that is 0 and the track must never translate: unlocking
     Tier 2 used to set tierPage = 1, sliding Tier 1 out of view with no nav
     buttons (they only render above two tiers) to bring it back. */
  function maxTierPage() { return Math.max(0, tiers().length - 2); }

  function unlockedCount() {
    var sub = eligibleSubtotal();
    return tiers().filter(function (t) { return sub >= t.amount * 100; }).length;
  }

  function isGift(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  /* Thresholds are measured on what the shopper actually paid for. Counting
     the gifts themselves would be harmless today (they are Rs.0) but would
     silently break the moment a gift carries a price. */
  function eligibleSubtotal() {
    if (!cart) return 0;
    /* Measure the ladder on what the shopper actually pays, so a Build Your
       Own Pack cart is judged on its Rs.699, not the Rs.1,038 of list prices.
       Gifts are subtracted so a priced gift could never fund its own tier. */
    var giftValue = cart.items.reduce(function (a, i) {
      return isGift(i) ? a + i.final_line_price : a;
    }, 0);
    return Math.max(0, subtotal() - giftValue);
  }

  function json(url, opts) {
    return fetch(url, opts).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) {
          var err = new Error((body && (body.description || body.message)) || 'Cart error');
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  function postJSON(url, payload) {
    return json(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  /* Full product record, cached — needed for compare_at_price (the struck
     MRP) and for the in-cart variant dropdown. /cart.js carries neither. */
  function getProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return json('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { productCache[handle] = null; return null; });
  }

  /* ---------- data ---------- */

  /* Warm the product records the tiles need (compare_at_price for the struck
     MRP, and the variant list for the pack-size picker). Cached per handle, so
     on a PDP this is already resolved by the page-load refresh and costs
     nothing on the add that follows. */
  function primeProducts(c) {
    var handles = [];
    c.items.forEach(function (i) {
      if (i.handle && handles.indexOf(i.handle) === -1) handles.push(i.handle);
    });
    return Promise.all(handles.map(getProduct));
  }

  /* Placeholder rows shown the instant the drawer opens, before /cart.js has
     answered. Without this the panel opened empty and stayed empty. */
  function skeletonHTML() {
    var row = '<div class="ecart-skel-tile">' +
      '<span class="ecart-skel-box"></span>' +
      '<span class="ecart-skel-lines"><i></i><i></i></span>' +
      '</div>';
    return '<div class="ecart-skel">' + row + row + '</div>';
  }

  /* ---------- "working" status strip ----------
     The cart is not instant and cannot be made instant: an add runs
     /cart/add.js, then convertCartToPack (which re-stamps every line), then a
     gift sync, then a re-read — measured at ~2.3-2.6s on this store, and the
     stale-stamp repair can add two more round trips. The skeleton rows and the
     bar across the top already say "something is happening"; neither says WHAT,
     so the wait reads as the site lagging rather than as work being done for
     the shopper.

     This names the work. The wording is deliberately kept true: the pack ladder
     really is being evaluated on every add (convertCartToPack -> packState),
     so "finding the best price" describes what the cart is actually doing, not
     a stall dressed up as a feature. If we ever stop repricing on add, these
     lines have to change with it.

     TWO TIMING GUARDS, both of which matter more than the animation:
       - nothing paints for STATUS_DELAY ms, so a fast cart never flashes it;
         a status that appears for 90ms is worse than no status at all.
       - once shown it stays for STATUS_MIN ms, so it cannot blink out
         mid-word on a cart that finishes just after the delay elapsed.
     Work is reference-counted because refresh() and the mutation helpers
     overlap constantly (every helper ends in refresh()); a plain boolean let
     the inner one clear the outer one's strip. */
  var STATUS_DELAY = 260;
  var STATUS_MIN = 300;      /* was 700 — it outlived a ready cart by 179ms */
  var STATUS_ROTATE = 1400;
  var STATUS_MAX = 12000;          /* hard stop: never leave it spinning forever */
  /* The headline never changes — it is the whole message, and rotating it meant
     the one thing worth reading was absent two thirds of the time. Only the
     sub-line cycles, and each line names work that genuinely runs: the ladder
     is evaluated, bundle savings compared, then the discount allocated. */
  var STATUS_TITLE = 'Finding the best deals for your cart';
  var STATUS_LINES = [
    'Checking pack prices',
    'Comparing bundle savings',
    'Applying your best price'
  ];

  var workDepth = 0, statusEl = null, statusIdx = 0;
  var pendingAdd = false, addFailSafe = null, provisional = false;
  var tShow = null, tRotate = null, tHide = null, tMax = null, shownAt = 0;

  function statusNode() {
    if (statusEl || !els.panel) return statusEl;
    /* Appended to the PANEL and absolutely positioned, not into .ecart-body:
       render() replaces the body's innerHTML on every paint, which would tear
       the overlay out mid-animation. Offset by the 60px header so the shopper
       keeps the title and the close button — covering those would trap them. It
       deliberately covers the FOOTER too: the total down there is stale until
       the repricing lands, and a stale total is worse than a hidden one. */
    statusEl = document.createElement('div');
    statusEl.className = 'ecart-finder';
    statusEl.setAttribute('role', 'status');
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.hidden = true;
    statusEl.innerHTML =
      '<div class="ecart-finder-card">' +
        '<span class="ecart-finder-stage" aria-hidden="true">' +
          '<span class="ecart-finder-chip"></span>' +
          '<span class="ecart-finder-chip"></span>' +
          '<span class="ecart-finder-chip"></span>' +
          '<span class="ecart-finder-glass">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
            'stroke-linecap="round" stroke-linejoin="round">' +
            '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 21 21"/>' +
            '</svg>' +
          '</span>' +
        '</span>' +
        '<span class="ecart-finder-copy">' +
          '<p class="ecart-finder-title">' + STATUS_TITLE + '</p>' +
          '<p class="ecart-finder-sub"></p>' +
        '</span>' +
        '<span class="ecart-finder-bar" aria-hidden="true"></span>' +
      '</div>';
    els.panel.appendChild(statusEl);
    return statusEl;
  }

  function paintStatus(i) {
    var n = statusNode(); if (!n) return;
    var t = n.querySelector('.ecart-finder-sub');
    if (!t) return;
    /* fade out, swap, fade in — the text must never change mid-read */
    n.classList.add('is-swapping');
    setTimeout(function () {
      t.textContent = STATUS_LINES[i % STATUS_LINES.length];
      n.classList.remove('is-swapping');
    }, 180);
  }

  function showStatus() {
    var n = statusNode(); if (!n) return;
    statusIdx = 0;
    n.querySelector('.ecart-finder-sub').textContent = STATUS_LINES[0];
    n.hidden = false;
    shownAt = Date.now();
    clearInterval(tRotate);
    tRotate = setInterval(function () { paintStatus(++statusIdx); }, STATUS_ROTATE);
    clearTimeout(tMax);
    tMax = setTimeout(function () { workDepth = 0; hideStatus(true); }, STATUS_MAX);
  }

  function hideStatus(force) {
    clearTimeout(tShow); tShow = null;
    var n = statusEl;
    if (!n || n.hidden) { clearInterval(tRotate); clearTimeout(tMax); return; }
    var held = Date.now() - shownAt;
    var finish = function () {
      clearInterval(tRotate); clearTimeout(tMax);
      if (n) n.hidden = true;
    };
    if (force || held >= STATUS_MIN) return finish();
    clearTimeout(tHide);
    tHide = setTimeout(finish, STATUS_MIN - held);
  }

  function workStart() {
    if (++workDepth !== 1) return;
    clearTimeout(tHide); clearTimeout(tShow);
    tShow = setTimeout(showStatus, STATUS_DELAY);
  }

  function workEnd() {
    if (--workDepth > 0) return;
    workDepth = 0;
    hideStatus(false);
  }

  /* THE ADD STARTS BEFORE WE HEAR ABOUT IT.
     On the PDP the add belongs to Dawn's product-form.js: it POSTs /cart/add.js
     itself and only calls cart-drawer.renderContents() once that resolves. Our
     work counter used to start there — i.e. at the END of the add. Measured
     cold: add.js alone ran 1088ms, refresh finished at 1883ms, and the overlay
     appeared at 1361ms. The shopper got the Add button's own spinner for the
     first second and the deal message only for the last third of the wait,
     which is exactly why it read as "missed".

     So we start the clock on the form SUBMIT, which fires the moment the button
     is pressed, and release it when renderContents' refresh settles. Capture
     phase, because product-form.js calls preventDefault on its own handler.

     Balanced deliberately: submit -> depth 1, refresh -> 2, refresh done -> 1,
     release -> 0. Forgetting the release would pin the overlay open forever. */
  /* ---------- optimistic add ----------
     /cart/add.js measures 728ms on this store (4 samples, 696-763) before any
     theme code runs, and /cart.js + product priming add ~180ms more. None of
     that is needed to know what the new line LOOKS like: the PDP already holds
     the variant, its price, its image and the product title. So draw the line
     from local data at the click and let the server confirm it afterwards.

     Two rules make this safe rather than a lie:
       - the provisional cart is never a source of truth. The moment /cart.js
         answers, refresh() overwrites it wholesale and clears `provisional`.
       - no TOTAL is ever shown against it. The pack ladder can move the total
         (1828 -> 1773.75 on a real cart), so the footer stays covered by the
         finder until the real numbers land. Showing a line is a promise we can
         keep; showing a total would be a guess presented as a fact.
     A failed add (sold out) never reaches renderContents, so the failsafe below
     reconciles against the server rather than leaving the guess on screen. */
  function pdpVariant(vid) {
    var root = document.querySelector('.bb[data-variants]');
    if (!root) return null;
    try {
      var vs = JSON.parse(root.getAttribute('data-variants')) || [];
      for (var i = 0; i < vs.length; i++) {
        if (String(vs[i].id) === String(vid)) return vs[i];
      }
    } catch (e) {}
    return null;
  }

  function optimisticAdd(vid, qty) {
    var v = pdpVariant(vid);
    if (!v) return false;                     /* no local data -> stay honest, show nothing */
    var handleMatch = location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = handleMatch ? decodeURIComponent(handleMatch[1]) : '';
    var og = document.querySelector('meta[property="og:title"]');
    var title = (og && og.content) || document.title;
    var img = (v.featured_image && v.featured_image.src) || '';
    if (!img) {
      var m = document.querySelector('meta[property="og:image"]');
      img = (m && m.content) || '';
    }
    var price = Number(v.price) || 0;

    var base = (cart && cart.items) ? JSON.parse(JSON.stringify(cart)) : { items: [], item_count: 0 };
    /* Already in the cart? Increment it — a second line would be a visible lie
       that the reconcile would then have to undo in front of the shopper. */
    var found = null;
    base.items.forEach(function (i) { if (String(i.variant_id) === String(vid)) found = i; });
    if (found) {
      found.quantity += qty;
      found.line_price = found.original_line_price = found.final_line_price = found.price * found.quantity;
    } else {
      base.items.push({
        key: '__provisional__' + vid, id: vid, variant_id: vid, quantity: qty,
        product_title: title, variant_title: (v.public_title || v.title || null),
        title: title, handle: handle, url: '/products/' + handle + '?variant=' + vid,
        price: price, final_price: price, original_price: price,
        line_price: price * qty, final_line_price: price * qty, original_line_price: price * qty,
        image: img, properties: {}, discounts: []
      });
    }
    base.item_count = base.items.reduce(function (a, i) { return a + i.quantity; }, 0);
    base.total_price = base.items.reduce(function (a, i) { return a + i.final_line_price; }, 0);
    base.original_total_price = base.total_price;
    base.items_subtotal_price = base.total_price;

    provisional = true;
    cart = base;
    openCart();
    render();          /* render() itself withholds the footer while provisional */
    return true;
  }

  function releaseAdd() {
    if (!pendingAdd) return;
    pendingAdd = false;
    clearTimeout(addFailSafe);
    workEnd();
  }

  /* THE ADD-TO-CART "HANG".
     Every add path (Dawn's product-form via renderContents, the PDP sticky bar,
     the BYOB builders) called refresh(true), and refresh only ran openCart()
     at the very END — after /cart.js, the gift-pool product fetches, a gift-sync
     mutation, a second /cart.js and one /products/<handle>.js per line. On a
     phone that is several seconds during which absolutely nothing moves, so the
     site reads as frozen. Dawn hides its own button spinner as soon as
     renderContents() returns (it is not awaited), which removed the last moving
     thing on screen.

     So: open the panel SYNCHRONOUSLY, show a skeleton if there is nothing to
     show yet, and paint real content as soon as the cart itself is known —
     the gift pool and gift sync repaint afterwards instead of blocking. */
  function refresh(open) {
    workStart();
    if (open) openCart();
    if (open || busy) {
      /* Skeleton whenever there is nothing real to show yet — NOT just on the
         very first open. `cart` stays truthy but EMPTY between an add and the
         /cart.js that reveals it, so the old `!cart` test let the drawer paint
         "Your cart is empty" for the whole of a slow add. With the status strip
         above it that reads as a flat contradiction: the cart announces it is
         finding deals for a cart it simultaneously says is empty. Placeholder
         rows are honest about the same state and become the real lines. */
      if ((!cart || !cart.items || !cart.items.length)
          && els.body && !els.body.querySelector('.ecart-skel')) {
        els.title.textContent = 'Your Cart';
        els.body.innerHTML = skeletonHTML();
        els.footWrap.hidden = true;
      }
      els.panel.classList.add('is-loading');
    }

    var done = function () { els.panel.classList.remove('is-loading'); };

    return json('/cart.js').then(function (c) {
      /* Wholesale overwrite: a provisional cart never survives a real one. */
      cart = c;
      provisional = false;
      return primeProducts(c);
    }).then(function () {
      /* First real paint. Gifts may repaint a moment later; a ladder that
         settles is far better than a drawer that shows nothing at all. */
      seedUnlocked();
      render();
      done();
      return resolveGiftPool();
    }).then(function () {
      /* Before the gift sync, not after: repairing a stale stamp folds a line
         back into the pack, which changes what the cart charges -- and the
         gift tiers are measured on exactly that. Getting these the wrong way
         round grants a tier against a subtotal that is about to move. */
      return repairPackStamps();
    }).then(function (repaired) {
      if (!repaired) return false;
      /* syncGifts() reads the module-level `cart`, which the repair just made
         stale. Re-read and repaint now so the ladder stops showing the count
         the broken stamp claimed. */
      return json('/cart.js').then(function (c2) {
        cart = c2;
        return primeProducts(c2);
      }).then(function () { render(); return true; });
    }).then(function (repaired) {
      return syncGifts().then(function (gifted) { return repaired || gifted; });
    }).then(function (changed) {
      if (!changed) return null;
      return json('/cart.js').then(function (c2) {
        cart = c2;
        return primeProducts(c2);
      });
    }).then(function () {
      render();
      checkTierUnlocks();
      return cart;
    }).catch(function (e) {
      console.error('[element-cart]', e);
      done();
      return null;
    }).then(function (r) { done(); workEnd(); return r; });
  }

  /* Mark tiers already cleared so we do not fire confetti on first paint. */
  function seedUnlocked() {
    if (booted) return;
    booted = true;
    var sub = eligibleSubtotal();
    tiers().forEach(function (t, i) { if (sub >= t.amount * 100) unlockedSeen[i] = true; });
  }

  /* cart.total_price is AFTER cart-level discounts; summing line prices is
     not. The Build Your Own Pack function applies an ORDER-level discount, so
     a pack cart charges e.g. Rs.699 while the lines still add to Rs.1,038 —
     summing lines overstates the total by the whole pack saving. */
  function subtotal() {
    if (!cart) return 0;
    return typeof cart.total_price === 'number'
      ? cart.total_price
      : cart.items.reduce(function (a, i) { return a + i.final_line_price; }, 0);
  }

  function cartDiscounts() {
    if (!cart || !cart.cart_level_discount_applications) return [];
    return cart.cart_level_discount_applications.map(function (d) {
      return { title: d.title || d.description || 'Discount', amount: d.total_allocated_amount };
    });
  }

  /* Struck-through reference total, from each line's compare_at_price. Not
     derivable from /cart.js, which carries no compare_at_price — it comes from
     the cached /products/<handle>.js record. */
  function mrpTotal() {
    if (!cart) return 0;
    return cart.items.reduce(function (a, i) {
      var v = variantOf(i);
      var unit = (v && v.compare_at_price && v.compare_at_price > v.price)
        ? v.compare_at_price
        : i.final_price;
      return a + unit * i.quantity;
    }, 0);
  }

  function variantOf(item) {
    var p = productCache[item.handle];
    if (!p) return null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].id === item.variant_id) return p.variants[i];
    }
    return null;
  }

  function isByob(item) {
    return !!(item.properties && (item.properties._byob || item.properties._byob_v));
  }

  /* ---------- Build Your Own Pack ladder ----------
     Prices for 2..5 units, in paise. MUST match the discount function's
     DEFAULT_TABLE — read off the PDP builder's data-pack when it is on the
     page so a retune follows automatically, with these as the fallback. */
  var PACK_LADDER = [74900, 99900, 129900, 149900];
  var PACK_TYPE = 'pack';

  function syncLadderFromBuilder() {
    var el = document.querySelector('[data-pack]');
    if (el) {
      var parts = String(el.getAttribute('data-pack')).split(',')
        .map(function (x) { return Math.round(parseFloat(x) * 100); });
      if (parts.length === 4 && parts.every(function (n) { return n > 0; })) PACK_LADDER = parts;
    }
    var t = document.querySelector('[data-packtype]');
    if (t && t.getAttribute('data-packtype')) PACK_TYPE = t.getAttribute('data-packtype');
  }

  /* Units a variant contributes to the pack ladder. custom.unit_count is
     authoritative; the title fallback mirrors the PDP builder's unitsFromTitle
     so an unmapped variant still counts sensibly (Pack of 2 = 2, 1 Month = 2,
     15 Days = 1) rather than defaulting to 1 and under-counting the pack. */
  function unitsFromTitle(t) {
    t = String(t || '').toLowerCase();
    var mp = t.match(/pack of\s*(\d+)/);
    if (mp) return parseInt(mp[1], 10) || 1;
    var mm = t.match(/(\d+)\s*month/);
    if (mm) return Math.max(1, (parseInt(mm[1], 10) || 1) * 2);
    var dd = t.match(/(\d+)\s*day/);
    if (dd) { var n = Math.round((parseInt(dd[1], 10) || 15) / 15); return n > 0 ? n : 1; }
    return 1;
  }

  function unitsForVariant(variantId, variantTitle) {
    var m = CFG.unitCounts || {};
    var v = m[String(variantId)];
    if (v != null) return Number(v) || 1;
    return unitsFromTitle(variantTitle);
  }

  function tierPrice(units) {
    if (units < 2) return null;
    return PACK_LADDER[Math.min(units, 5) - 2];
  }

  /* Pack-eligible contents of the cart, counted the way the discount function
     counts: by UNITS (unit_count x quantity), not by lines.

     ANY cart counts, not just one already tagged _byob — so the rail always
     quotes the ladder and the first add folds the existing lines into the
     pack. Gifts are excluded: they are Rs.0 and must never be repriced. */
  function packState() {
    if (!cart || !cart.items.length) return null;
    var units = 0, sum = 0, n = 0;
    cart.items.forEach(function (i) {
      if (isGift(i)) return;
      var per = isByob(i)
        ? (parseInt(i.properties._byob_units, 10) || 1)
        : unitsForVariant(i.variant_id, i.variant_title);
      units += per * i.quantity;
      sum += i.final_line_price;
      n++;
    });
    return n ? { units: units, sum: sum } : null;
  }

  /* Retro-fit the pack stamp onto lines the shopper added normally, so the
     whole cart prices on the ladder. change.js CAN set properties on a line
     already in the cart, so nothing has to be removed and re-added — but it
     rotates every key, so the cart is re-read between each line. */
  /* ---------- what each pack line ACTUALLY costs ----------

     The Build Your Own Pack discount is an ORDER-level Shopify Function, so
     every line in /cart.js still carries its full sale price and the entire
     saving surfaces only as a cart-level discount beneath the total. That is
     the contradiction the shopper hit: "Boost your results" quoted the ladder
     STEP (Rs.300), they tapped Add, and the line then read Rs.599 with the
     discount buried at the bottom of the drawer.

     Price each pack line at the step it actually caused instead:
     charged(units through this line) - charged(units before it). That is the
     SAME formula ladderDelta() quotes the rail with, so the number the shopper
     was shown is the number they then see. The steps telescope to
     charged(all units) = cart.total_price, so the lines still add up.

     Returns null whenever the split cannot be trusted, in which case the tiles
     fall back to raw line prices exactly as before. */
  var allocMap = null;

  function chargedThrough(saleSum, units) {
    var t = tierPrice(units);
    return (t === null) ? saleSum : Math.min(saleSum, t);
  }

  function packAllocation() {
    if (!cart || !cart.items.length) return null;

    var disc = (cart.cart_level_discount_applications || []).reduce(function (a, d) {
      return a + (d.total_allocated_amount || 0);
    }, 0);
    if (disc <= 0) return null;              /* nothing discounted, nothing to spread */

    /* The pack discount only ever touches _byob lines, so split the paid cart:
       tagged lines share the discount, loose lines keep their own price.

       This used to bail out entirely whenever ANY untagged line was present,
       which turned out to be far more damaging than the case it guarded
       against. Once the ladder is full at 5 units the rail adds the next item
       UNTAGGED, so the guard fired on a perfectly ordinary cart: every pack
       line snapped back from its allocated price to its raw one (a Rs.81 line
       jumped to Rs.449) and the lines then summed to Rs.2,166 against a
       Rs.1,798 charge. */
    var paid = cart.items.filter(function (i) { return !isGift(i); });
    var lines = paid.filter(isByob);
    var loose = paid.filter(function (i) { return !isByob(i); });
    if (!lines.length) return null;

    var looseSum = loose.reduce(function (a, i) { return a + i.final_line_price; }, 0);
    var gross = lines.reduce(function (a, i) { return a + i.final_line_price; }, 0);
    var target = gross - disc;                /* what the tagged lines really cost */
    if (target <= 0 || target > gross) return null;

    /* Attributing the whole cart-level discount to the pack is true for the
       Build Your Own Pack function but not for anything else that might
       discount at cart level. Prove it rather than assume it: the loose lines
       plus the pack must equal what the cart actually charges, or we do not
       understand this cart and must not restate its prices. */
    if (looseSum + target !== subtotal()) return null;

    var out = {}, i, ln;

    /* Split the lines into the ones carrying a quote and the ones that do not.
       Cart order cannot stand in for "which one did they just add": change.js
       re-stamping in convertCartToPack() moves the older lines to the END, so
       walking the cart in order charged the NEW item as if it were the first
       one in the pack -- the serum quoted at Rs.400 rendered as Rs.519. */
    var quotedSum = 0, quotedLines = [], plainLines = [];
    for (i = 0; i < lines.length; i++) {
      ln = lines[i];
      var q = parseInt(ln.properties._byob_quoted, 10);
      if (!isNaN(q) && q >= 0) { quotedLines.push(ln); quotedSum += q; }
      else plainLines.push(ln);
    }

    /* Honour the quotes literally and let the remaining lines absorb the rest.
       If the quotes alone already exceed what the cart charges they cannot all
       be true any more (the pack repriced under them), so fall back to pricing
       every line by the ladder. */
    /* A quote is a promise about the cart it was made against, and removing the
       line that anchored the pack invalidates it: what is left is a different
       pack at a different ladder step. The tell is that EVERY remaining line
       carries a quote, so there is no un-quoted line left to absorb the
       difference -- honouring them all made two lines read Rs.200 + Rs.400
       against a Rs.699 charge. Only trust the quotes while something else can
       still carry the remainder. */
    if (quotedLines.length && plainLines.length && quotedSum <= target) {
      for (i = 0; i < quotedLines.length; i++) {
        out[quotedLines[i].key] = parseInt(quotedLines[i].properties._byob_quoted, 10);
      }
      out = spread(plainLines, target - quotedSum, out);
    } else {
      out = spread(lines, target, {});
    }

    /* Hard invariant: the lines MUST add up to what the cart charges. A split
       that does not is a drawer contradicting its own total, which is the whole
       class of bug this function exists to remove -- so verify it here rather
       than trusting every path above, and fall back to a pro-rata split, which
       reconciles by construction. */
    var check = 0;
    for (i = 0; i < lines.length; i++) check += out[lines[i].key] || 0;
    if (check !== target) {
      out = {};
      var run = 0;
      for (i = 0; i < lines.length; i++) {
        ln = lines[i];
        out[ln.key] = Math.max(0, (i === lines.length - 1)
          ? target - run
          : Math.round(target * (ln.final_line_price / (gross || 1))));
        run += out[ln.key];
      }
    }
    return out;

    /* Marginal walk over `set`, then reconciled so the lines always add up to
       `want`. PACK_LADDER mirrors the discount function's table by hand; if it
       has drifted, absorb a rounding rupee on the last line and fall back to a
       pro-rata split for anything larger, which reconciles by construction. */
    function spread(set, want, acc2) {
      if (!set.length) return acc2;
      var saleSum = 0, units = 0, prev = 0, sum = 0, j, l, per, now, step;
      var setGross = set.reduce(function (a, x) { return a + x.final_line_price; }, 0);

      /* If these lines already cost exactly what is left to account for, leave
         them alone. Adding something must never restate the price of what was
         already in the cart. */
      if (setGross === want) {
        for (j = 0; j < set.length; j++) acc2[set[j].key] = set[j].final_line_price;
        return acc2;
      }
      for (j = 0; j < set.length; j++) {
        l = set[j];
        per = parseInt(l.properties._byob_units, 10) ||
              unitsForVariant(l.variant_id, l.variant_title) || 1;
        saleSum += l.final_line_price;
        units += per * l.quantity;
        now = chargedThrough(saleSum, units);
        step = now - prev;
        prev = now;
        acc2[l.key] = step < 0 ? 0 : step;
        sum += acc2[l.key];
      }
      var drift = want - sum;
      if (drift !== 0 && Math.abs(drift) <= 100) {
        var lastKey = set[set.length - 1].key;
        acc2[lastKey] = Math.max(0, acc2[lastKey] + drift);
      } else if (drift !== 0) {
        var run = 0;
        for (j = 0; j < set.length; j++) {
          l = set[j];
          acc2[l.key] = Math.max(0, (j === set.length - 1)
            ? want - run
            : Math.round(want * (l.final_line_price / (setGross || 1))));
          run += acc2[l.key];
        }
      }
      return acc2;
    }
  }

  /* Fold every untagged paid line into the pack in a FIXED number of requests:
     one /cart/add.js re-adds them all carrying the pack stamp, one
     /cart/update.js then zeroes the originals. Shopify keeps the re-added lines
     separate from the originals because the properties differ, so the two sets
     never merge.

     The per-line loop below costs ~900ms PER LINE because /cart/change.js can
     only stamp one line at a time and rotates every key, forcing a re-read
     between each. Measured on this store, click to converted cart:
        2 existing lines   3,201ms -> 2,596ms
        4 existing lines   5,959ms -> 2,292ms      (identical carts both ways)
     i.e. flat instead of linear.

     ADD BEFORE REMOVE, always: removing first loses the lines outright if the
     add fails. The cost of that ordering is that each quantity is briefly
     doubled, so a shopper holding the last units of a low-stock SKU can get a
     422 — hence the fallback to the original per-line path, which mutates one
     line at a time and never doubles anything. */
  function convertCartToPack() {
    return json('/cart.js').then(function (c) {
      var pending = c.items.filter(function (i) { return !isGift(i) && !isByob(i); });
      if (!pending.length) return c;

      var items = pending.map(function (i) {
        var props = {};
        Object.keys(i.properties || {}).forEach(function (k) { props[k] = i.properties[k]; });
        props._byob = PACK_TYPE;
        props._byob_v = String(i.variant_id);
        props._byob_units = String(unitsForVariant(i.variant_id, i.variant_title));
        return { id: i.variant_id, quantity: i.quantity, properties: props };
      });

      return postJSON('/cart/add.js', { items: items })
        .then(function () { return json('/cart.js'); })
        .then(function (fresh) {
          /* Keys rotated during the add, so the originals are re-found here and
             zeroed in ONE update.js — evaluated against a single consistent
             cart, unlike a sequence of change.js calls. */
          var zero = {}, any = false;
          fresh.items.forEach(function (i) {
            if (!isGift(i) && !isByob(i)) { zero[i.key] = 0; any = true; }
          });
          if (!any) return fresh;
          return postJSON('/cart/update.js', { updates: zero });
        })
        .catch(function (e) {
          console.warn('[element-cart] batched pack conversion failed, falling back to per-line', e);
          return convertPerLine();
        });
    });
  }

  function convertPerLine() {
    var pass = function (depth) {
      if (depth > 12) return Promise.resolve();          /* runaway guard */
      return json('/cart.js').then(function (fresh) {
        var target = null;
        fresh.items.forEach(function (i) {
          if (!target && !isGift(i) && !isByob(i)) target = i;
        });
        if (!target) return null;
        var props = {};
        Object.keys(target.properties || {}).forEach(function (k) { props[k] = target.properties[k]; });
        props._byob = PACK_TYPE;
        props._byob_v = String(target.variant_id);
        props._byob_units = String(unitsForVariant(target.variant_id, target.variant_title));
        return postJSON('/cart/change.js', {
          id: target.key, quantity: target.quantity, properties: props
        }).then(function () { return pass(depth + 1); });
      });
    };
    return pass(0);
  }


  /* ---------- stale pack stamps ----------
     A pack line carries _byob_v (the variant it was tagged on) and _byob_units
     (what that variant counts as). The discount function SKIPS any tagged line
     whose CURRENT variant no longer matches _byob_v -- the anti-upgrade guard,
     so nobody can tag the Rs.449 variant into a pack and then swap up to the
     Rs.1,099 one while still paying the ladder price the small one earned.

     switchVariant() keeps the pair in step for swaps made in this drawer. A
     swap made anywhere else does not: the GoKwik checkout carries a line's
     properties across while repointing the variant, which lands a line whose
     variant and stamp disagree. Seen on TE25-45421 (2026-08-29): a "2 Month
     Pack" line (4 units) still stamped _byob_v/_byob_units from the "1 Month
     Pack" (2 units). isByob() still calls that a pack line, so this drawer
     counted it into the ladder at the stale count of 2 while the function
     dropped it from the pack outright and charged every line in full.

     Repairing means re-stamping to the CURRENT variant with recomputed units
     -- exactly what switchVariant() does, and safe for the same reason: the
     units move too, so a line that swapped UP raises the total unit count and
     lands on a HIGHER ladder step rather than riding the old one. A downgrade
     drops the tier, which is equally right: the pack should price what is
     actually in it.

     Costs nothing in the normal case -- a scan of the cart already in hand,
     with no request unless a line genuinely disagrees. */
  function staleStamps(c) {
    if (!c || !c.items) return [];
    return c.items.filter(function (i) {
      if (isGift(i) || !isByob(i)) return false;
      var stamped = i.properties && i.properties._byob_v;
      return !!stamped && String(stamped) !== String(i.variant_id);
    });
  }

  function repairPackStamps() {
    if (!cart || !staleStamps(cart).length) return Promise.resolve(false);

    /* One line at a time, re-reading between: change.js rotates every key, so
       a batch built against the cart we hold would 400 on the second line.
       Mismatches are rare and arrive one at a time, so the per-line cost that
       convertCartToPack() had to engineer away does not apply here. */
    var fixed = false;
    var pass = function (depth) {
      if (depth > 12) return Promise.resolve();      /* runaway guard */
      return json('/cart.js').then(function (fresh) {
        var target = staleStamps(fresh)[0];
        if (!target) return null;

        var props = {};
        Object.keys(target.properties || {}).forEach(function (k) { props[k] = target.properties[k]; });
        props._byob = target.properties._byob || PACK_TYPE;
        props._byob_v = String(target.variant_id);
        props._byob_units = String(unitsForVariant(target.variant_id, target.variant_title));
        /* The quote was a promise about the pack this line USED to be in, and
           the variant changed under it. Drop it rather than honour it: a stale
           quote makes packAllocation() split a total the line no longer earns. */
        delete props._byob_quoted;

        return postJSON('/cart/change.js', {
          id: target.key, quantity: target.quantity, properties: props
        }).then(function () { fixed = true; return pass(depth + 1); });
      });
    };

    return pass(0)
      .then(function () { return fixed; })
      .catch(function (e) {
        /* Never block the drawer on this. An unrepaired line stays OUT of the
           pack and charges full price -- wrong in our favour, and safe to let
           through, unlike trapping the shopper in the cart. */
        console.warn('[element-cart] pack stamp repair failed', e);
        return fixed;
      });
  }
  var giftSyncing = false;
  var giftWarned = false;

  function syncGifts() {
    if (!CFG.giftsEnabled) return Promise.resolve(false);
    if (giftSyncing || !cart) return Promise.resolve(false);

    var T = tiers();
    var earned = unlockedCount();
    var ops = [];
    var current = {};

    cart.items.forEach(function (it) {
      if (!isGift(it)) return;
      var tier = it.properties[GIFT_PROP];
      if (current[tier]) { ops.push({ id: it.key, quantity: 0 }); return; }  /* duplicate */
      current[tier] = it;
    });

    /* Drop gifts whose tier is no longer earned (or that are no longer in that
       tier's pool), and pin the rest to qty 1. */
    Object.keys(current).forEach(function (tierName) {
      var idx = -1;
      for (var i = 0; i < T.length; i++) if (T[i].name === tierName) idx = i;
      var inTierPool = idx > -1 && poolForTier(idx).some(function (g) {
        return g.variantId === current[tierName].variant_id;
      });
      var stale = idx === -1 || idx >= earned || !inTierPool;
      if (stale) {
        ops.push({ id: current[tierName].key, quantity: 0 });
        delete current[tierName];
      } else if (current[tierName].quantity !== 1) {
        ops.push({ id: current[tierName].key, quantity: 1 });
      }
    });

    var used = [];
    Object.keys(current).forEach(function (k) { used.push(current[k].variant_id); });

    /* Pre-selection is the ONLY part gated on auto_add_gifts. Revoking, qty
       pinning and duplicate culling above run unconditionally — gating those
       too would let a shopper qualify, claim a gift, empty the cart and keep
       the free product. */
    var toAdd = [];
    for (var i = 0; CFG.autoAddGifts && i < earned && i < T.length; i++) {
      if (current[T[i].name]) continue;
      var tierPool = poolForTier(i);
      var pick = null;
      for (var j = 0; j < tierPool.length; j++) {
        if (used.indexOf(tierPool[j].variantId) === -1) { pick = tierPool[j]; break; }
      }
      if (!pick) break;
      used.push(pick.variantId);
      var props = {};
      props[GIFT_PROP] = T[i].name;
      toAdd.push({ id: pick.variantId, quantity: 1, properties: props });
    }

    if (!ops.length && !toAdd.length) return Promise.resolve(false);
    return runGiftOps(ops, toAdd);
  }

  function runGiftOps(ops, toAdd) {
    giftSyncing = true;

    /* SAFETY NET: gift logic may only ever touch gift lines. Every key is
       re-checked against the live cart, so a stale or miscomputed key can
       never zero a line the shopper actually chose. */
    var giftKeys = {};
    (cart ? cart.items : []).forEach(function (i) { if (isGift(i)) giftKeys[i.key] = true; });
    var safe = ops.filter(function (op) {
      if (giftKeys[op.id]) return true;
      console.warn('[element-cart] refusing to touch a non-gift line', op.id);
      return false;
    });

    var chain = Promise.resolve();
    if (safe.length) {
      /* ONE update.js call, not a change.js per line: Shopify rotates every
         line key on each mutation, so later keys in a sequence are stale. */
      var updates = {};
      safe.forEach(function (op) { updates[op.id] = op.quantity; });
      chain = chain.then(function () { return postJSON('/cart/update.js', { updates: updates }); });
    }
    if (toAdd && toAdd.length) {
      chain = chain.then(function () { return postJSON('/cart/add.js', { items: toAdd }); });
    }
    return chain
      .catch(function (e) {
        console.warn('[element-cart] gift sync failed', e);
        if (!giftWarned) { giftWarned = true; toast('That free gift is out of stock right now'); }
      })
      .then(function () { giftSyncing = false; return true; });
  }

  /* Shopper picked a different gift for a tier. If the chosen gift is held by
     another tier we drop that one too and let the next sync refill it. */
  function chooseGift(tierIdx, variantId) {
    var T = tiers();
    if (!T[tierIdx] || busy) return;
    var name = T[tierIdx].name;
    var vid = Number(variantId);
    if (chosenFor(name) && chosenFor(name).variant_id === vid) { pickerFor = null; render(); return; }

    setBusy(true);
    var ops = [];
    cart.items.forEach(function (it) {
      if (!isGift(it)) return;
      if (it.properties[GIFT_PROP] === name || it.variant_id === vid) ops.push({ id: it.key, quantity: 0 });
    });
    var props = {};
    props[GIFT_PROP] = name;

    runGiftOps(ops, [{ id: vid, quantity: 1, properties: props }])
      .then(function () { pickerFor = null; return refresh(false); })
      .then(function () { setBusy(false); });
  }

  /* What adding this item ACTUALLY costs: the cart's charge after the add,
     minus what it charges right now.

     The baseline must be the real current total (cart.total_price), NOT the
     hypothetical pack price of the current lines. On a cart that has not been
     converted yet those differ: two plain singles charge Rs.1,058 today, and
     assuming the Rs.699 pack rate as the baseline quoted Rs.300 for an add
     that in fact took the cart DOWN to Rs.999 — a Rs.359 error, in the
     shopper's favour but still a lie.

     Returns null past 5 units, where extras bill at their sale price and the
     ladder no longer describes the charge. Never negative: an add that lowers
     the total reads as FREE. */
  function ladderDelta(addPrice, addUnits) {
    var st = packState();
    if (!st) return null;
    var after = st.units + (addUnits || 1);
    if (after > 5) return null;
    var tier = tierPrice(after);
    var charged = (tier === null) ? (st.sum + addPrice) : Math.min(st.sum + addPrice, tier);
    var d = charged - subtotal();
    return d < 0 ? 0 : d;
  }

  /* ---------- render ---------- */

  function render() {
    if (!cart) return;
    /* One allocation per paint: itemsHTML() and paintFooter() must agree on
       whether the pack saving is already shown on the lines. */
    allocMap = packAllocation();
    /* Count distinct SKUs (cart lines), NOT cart.item_count. item_count sums
       quantities, so a "Pack of 2" line and a Build Your Own Pack inflate it
       past the number of things the shopper recognises in the cart. */
    var n = cart.items.length;
    var sub = subtotal();
    var mrp = mrpTotal();

    els.title.textContent = n === 0
      ? 'Your Cart'
      : 'Your Cart (' + n + ' item' + (n === 1 ? '' : 's') + ')';

    els.body.innerHTML = n === 0
      ? emptyHTML()
      : itemsHTML() + giftsHTML(eligibleSubtotal()) + upsellHTML();
    els.sheet.innerHTML = n === 0 ? '' : pickerHTML();

    /* A PROVISIONAL CART MUST NEVER SHOW A TOTAL.
       The line is drawn from local data and is safe to show; the total is not,
       because the pack ladder can move it (1828 -> 1773.75 on a real cart). The
       rule has to live HERE rather than as a one-off assignment at the optimistic
       add: any later repaint — a gift-pool resolve, a tier repaint, the failsafe's
       reconcile — would otherwise silently reinstate a total we are only guessing
       at. Enforced at the render layer, nothing downstream can undo it. */
    els.footWrap.hidden = (n === 0) || provisional;
    if (n > 0 && !provisional) paintFooter(sub, mrp);

    syncBubbles(n);
    wire();
    requestAnimationFrame(paintBars);
  }

  function emptyHTML() {
    return '<div class="ecart-empty">' +
      '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="rgba(18,18,18,.3)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>' +
      '<strong>Your cart is empty</strong>' +
      '<p>Add something and the gift ladder starts filling up.</p>' +
      '<a href="/collections/all">Browse bestsellers</a>' +
      '</div>';
  }

  function itemsHTML() {
    return '<div class="ecart-items">' + cart.items.map(function (item, idx) {
      if (isGift(item)) return giftLineHTML(item);
      var p = productCache[item.handle];
      var v = variantOf(item);
      /* Pack lines are priced at the ladder step they cost, so the tile shows
         what "Boost your results" quoted rather than the undiscounted sale
         price the order-level discount function leaves on the line. */
      var allocLine = (allocMap && allocMap[item.key] != null) ? allocMap[item.key] : null;
      var priced = allocLine !== null;
      var unit = priced
        ? Math.round(allocLine / Math.max(1, item.quantity))
        : item.final_price;
      /* Struck reference stays the true MRP, falling back to the line's own
         sale price so a pack line never shows a discount with nothing struck
         through beside it. */
      var mrpUnit = (v && v.compare_at_price && v.compare_at_price > v.price)
        ? v.compare_at_price
        : (priced && item.final_price > unit ? item.final_price : 0);
      var off = (mrpUnit && mrpUnit > unit) ? pctOff(mrpUnit, unit) : 0;
      var img = item.image
        ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2')
        : '';

      var picker = '';
      /* Pack lines KEEP their variant picker. The discount function skips a
         tagged line whose variant no longer matches _byob_v, so switchVariant()
         re-stamps _byob_v AND _byob_units onto the new variant — the swap stays
         in the pack and the ladder re-counts, instead of the line silently
         dropping out to full price. */
      if (p && p.variants.length > 1) {
        picker = '<span class="ecart-variant"><select data-ec-variant="' + esc(item.key) + '" aria-label="Choose pack size">' +
          p.variants.map(function (vv) {
            return '<option value="' + vv.id + '"' +
              (vv.id === item.variant_id ? ' selected' : '') +
              (vv.available ? '' : ' disabled') + '>' +
              esc(vv.title) + (vv.available ? '' : ' — sold out') + '</option>';
          }).join('') + '</select></span>';
      }

      return '<div class="ecart-tile">' +
        '<a class="ecart-thumb" href="' + esc(item.url) + '">' +
          (img ? '<img src="' + esc(img) + '" alt="' + esc(item.product_title) + '" loading="lazy" width="62" height="62">' : '') +
        '</a>' +
        '<div class="ecart-tile-det">' +
          '<a class="ecart-title" href="' + esc(item.url) + '" title="' + esc(item.product_title) + '">' +
            esc(displayName(item.handle, item.product_title)) + '</a>' +
          picker +
          (isByob(item)
            ? '<span class="ecart-packflag">' + (priced ? 'pack price' : 'In pack') + '</span>'
            : '') +
        '</div>' +
        '<div class="ecart-tile-right">' +
          '<div class="ecart-price">' +
            /* MRP sits beside the price, not above it — stacking all three
               made the row 59px tall next to a 17px name. */
            '<span class="ecart-price-row">' +
              (mrpUnit ? '<s class="ecart-strike">' + money(mrpUnit) + '</s>' : '') +
              '<span class="ecart-final">' + money(unit) + '</span>' +
            '</span>' +
            (off ? '<span class="ecart-off">(' + off + '% OFF)</span>' : '') +
          '</div>' +
          '<div class="ecart-qtywrap">' +
            '<div class="ecart-qty">' +
              '<button type="button" data-ec-dec="' + esc(item.key) + '" aria-label="Decrease quantity"' + (item.quantity <= 1 ? ' disabled' : '') + '>\u2212</button>' +
              '<input type="number" min="1" step="1" inputmode="numeric" value="' + item.quantity + '" data-ec-qty="' + esc(item.key) + '" aria-label="Quantity">' +
              '<button type="button" data-ec-inc="' + esc(item.key) + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="ecart-del" data-ec-del="' + esc(item.key) + '" aria-label="Remove ' + esc(item.product_title) + '">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zm2-4h2V8H9zm4 0h2V8h-2z"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    }).join('') + '</div>';
  }

  /* A granted gift is shown as a locked row: no stepper, no delete. It is
     removed automatically when the cart drops below its threshold. */
  function giftLineHTML(item) {
    var img = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2') : '';
    return '<div class="ecart-tile is-gift">' +
      '<span class="ecart-thumb">' +
        (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="62" height="62">' : '') +
      '</span>' +
      '<div class="ecart-tile-det">' +
        '<span class="ecart-title" title="' + esc(item.product_title) + '">' +
          esc(displayName(item.handle, item.product_title)) + '</span>' +
        '<span class="ecart-giftrow">' +
          '<span class="ecart-giftflag">' + esc(item.properties[GIFT_PROP]) + ' reward</span>' +
        '</span>' +
      '</div>' +
      '<div class="ecart-tile-right">' +
        '<div class="ecart-price"><span class="ecart-freetag">FREE</span></div>' +
      '</div>' +
    '</div>';
  }

  function giftsHTML(sub) {
    var T = tiers();
    if (!CFG.giftsEnabled || !T.length || !giftPool().length) return '';
    if (tierPage > maxTierPage()) tierPage = maxTierPage();

    var cards = T.map(function (t, i) {
      var target = t.amount * 100;
      var on = sub >= target;
      var pct = Math.min(sub / target, 1) * 100;
      var line = chosenFor(t.name);
      var gift = line ? poolByVariant(line.variant_id) : null;

      var art = on && gift && gift.image
        ? '<img class="ecart-gphoto" src="' + esc(gift.image) + '" alt="" width="46" height="46">'
        : giftBoxSVG(on);

      var body;
      if (on && gift) {
        /* Claimed: name the gift and offer the swap. */
        body =
          '<span class="ecart-tier">' + esc(t.name) + ' &middot; unlocked</span>' +
          '<p class="ecart-gcopy is-won" title="' + esc(gift.fullTitle) + '">' + esc(gift.title) + '</p>' +
          (poolForTier(i).length > 1
            ? '<button type="button" class="ecart-gchange" data-ec-picker="' + i + '">Change gift</button>'
            : '');
      } else if (on) {
        /* Earned but unclaimed: full bar plus the call to action. */
        body =
          '<span class="ecart-tier">' + esc(t.name) + '</span>' +
          '<div class="ecart-bar"><div class="ecart-fill" data-w="100"></div></div>' +
          '<p class="ecart-gcopy">Choose your free gift</p>' +
          (poolForTier(i).length
            ? '<button type="button" class="ecart-gselect" data-ec-picker="' + i + '">Select 1 Free Gift</button>'
            : '<span class="ecart-gnone">No gift available</span>');
      } else {
        body =
          '<span class="ecart-tier">' + esc(t.name) + '</span>' +
          '<div class="ecart-bar"><div class="ecart-fill" data-w="' + pct + '"></div></div>' +
          '<p class="ecart-gcopy">' + moneyFlat(target - sub) + ' more = ' +
            (i === 0 ? '1 free gift' : '1 more gift') + '!</p>';
      }

      return '<div class="ecart-gcard' + (on ? ' is-unlocked' : '') + '">' +
        '<div class="ecart-gimg">' + art +
          (on ? '' : '<span class="ecart-lock">' +
            '<svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true"><path fill="#6B7280" d="M12 1a5 5 0 0 1 5 5v3h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3v3h6V6a3 3 0 0 0-3-3"/></svg>' +
          '</span>') +
        '</div>' +
        '<div class="ecart-gcard-body">' + body + '</div>' +
      '</div>';
    }).join('');

    var nav = T.length > 2
      ? '<div class="ecart-gift-nav">' +
          '<button type="button" data-ec-tier="-1" aria-label="Previous gifts"' + (tierPage <= 0 ? ' disabled' : '') + '>&lsaquo;</button>' +
          '<button type="button" data-ec-tier="1" aria-label="Next gifts"' + (tierPage >= T.length - 1 ? ' disabled' : '') + '>&rsaquo;</button>' +
        '</div>'
      : '';

    return '<div class="ecart-gifts" style="background:' + esc(CFG.giftBg || '#3CB9F7') + '">' +
      '<div class="ecart-gift-head"><h2>' + esc(CFG.giftTitle || 'Choose your free gift') + '</h2>' + nav + '</div>' +
      '<div class="ecart-slider"><div class="ecart-track" data-ec-track>' + cards + '</div></div>' +
    '</div>';
  }

  function giftBoxSVG(on) {
    return '<svg width="38" height="38" viewBox="0 0 24 24" aria-hidden="true">' +
      '<rect x="2.5" y="9" width="19" height="12.5" rx="1.6" fill="' + (on ? '#00CC66' : '#9AA3AE') + '"/>' +
      '<rect x="1.5" y="6" width="21" height="4" rx="1.2" fill="' + (on ? '#00B85C' : '#8A939E') + '"/>' +
      '<rect x="10.3" y="6" width="3.4" height="15.5" fill="#fff" opacity=".85"/>' +
    '</svg>';
  }

  /* Gift chooser, modelled on the GoKwik sheet: it covers the drawer so each
     option gets a full-width row with its own image, pack size, struck value
     and Add button — a half-width tier card has nowhere near the room. */
  function pickerHTML() {
    var T = tiers();
    if (pickerFor === null || !T[pickerFor]) return '';
    var name = T[pickerFor].name;
    var mine = chosenFor(name);
    var takenElsewhere = {};
    cart.items.forEach(function (it) {
      if (isGift(it) && it.properties[GIFT_PROP] !== name) takenElsewhere[it.variant_id] = it.properties[GIFT_PROP];
    });

    var rows = poolForTier(pickerFor).map(function (g) {
      var on = mine && mine.variant_id === g.variantId;
      var taken = takenElsewhere[g.variantId];
      return '<div class="ecart-grow' + (on ? ' is-on' : '') + '">' +
        '<span class="ecart-grow-img">' +
          (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy" width="56" height="56">' : '') +
        '</span>' +
        '<div class="ecart-grow-det">' +
          '<p class="ecart-grow-name" title="' + esc(g.fullTitle) + '">\uD83C\uDF81 ' + esc(g.title) + '</p>' +
          (g.variantTitle ? '<span class="ecart-grow-pill">' + esc(g.variantTitle) + '</span>' : '') +
          (taken ? '<span class="ecart-grow-note">already your ' + esc(taken) + ' gift</span>' : '') +
        '</div>' +
        '<div class="ecart-grow-right">' +
          (g.mrp ? '<s class="ecart-grow-mrp">' + money(g.mrp) + '</s>' : '') +
          '<span class="ecart-grow-free">FREE</span>' +
          (on
            ? '<button type="button" class="ecart-grow-add is-on" disabled>&#10003; Added</button>'
            : taken
              ? '<button type="button" class="ecart-grow-add is-taken" disabled>Claimed</button>'
              : '<button type="button" class="ecart-grow-add" data-ec-pick="' + g.variantId + '" data-ec-pick-tier="' + pickerFor + '">+ Add</button>') +
        '</div>' +
      '</div>';
    }).join('');

    return '<div class="ecart-sheet">' +
      '<div class="ecart-sheet-dim" data-ec-picker-close></div>' +
      '<div class="ecart-sheet-panel" role="dialog" aria-label="Choose your free gift">' +
        '<button type="button" class="ecart-sheet-x" data-ec-picker-close aria-label="Close gift picker">&times;</button>' +
        '<h3 class="ecart-sheet-title">Choose Any 1 Free Gift</h3>' +
        '<div class="ecart-sheet-list">' + rows + '</div>' +
      '</div>' +
    '</div>';
  }

  /* Mirrors the Liquid variant rule so a client-side recommendation prices and
     carts the same variant: Combo/Kit on bundles, else the standard single
     unit, never blind cheapest. */
  function pickVariant(p) {
    var avail = p.variants.filter(function (v) { return v.available; });
    if (!avail.length) return null;
    var byTitle = function (re, neg) {
      for (var i = 0; i < avail.length; i++) {
        var t = String(avail[i].title).toLowerCase();
        if (re.test(t) && !(neg && neg.test(t))) return avail[i];
      }
      return null;
    };
    return byTitle(/combo|kit/)
        || byTitle(/single|pack of 1\b|1[\s-]*month/, /pack of [2-9]|[2-9]\s*month|travel|mini|sample|trial|sachet/)
        || avail.filter(function (v) { return !/travel|mini|sample|trial|sachet/.test(String(v.title).toLowerCase()); })[0]
        || avail[0];
  }

  /* Recommendations come from custom.bundle_addons, keyed by the highest-value
     product in the cart — the same curated list the PDP frequently-bought-
     together uses, so the two surfaces agree. Shopify's /recommendations API
     was tried first and returned a single product for a combo anchor, which is
     worse than the fallback collection. */
  var addonFetched = {};

  function anchorItem() {
    if (!cart || !cart.items.length) return null;
    var anchor = null, best = -1;
    cart.items.forEach(function (i) {
      if (isGift(i)) return;
      if (i.final_line_price > best) { best = i.final_line_price; anchor = i; }
    });
    return anchor;
  }

  function recommendedFor() {
    var rail = CFG.addonRail || {};
    var anchor = anchorItem();
    if (!anchor) return null;
    var list = rail[anchor.product_id] || rail[String(anchor.product_id)];
    list = (list || []).filter(Boolean);
    if (list.length) return list;
    ensureAddons(anchor);
    return null;
  }

  /* addonRail is rendered from Liquid, so it can only hold the products that
     were in the cart AT PAGE LOAD plus the current PDP product. Everything
     added by AJAX afterwards -- which is most of what ends up in this cart --
     had no entry, so the rail fell through to the generic upsell collection
     and started recommending combos. Reported as: add the underarm duo, add
     the HA serum, remove the duo, and the rail fills with combos.

     Fetch the curated list for the anchor on demand instead, cache it, and
     repaint. One request per product per page, and the flag is set before the
     repaint so a miss cannot loop. */
  function ensureAddons(anchor) {
    var id = String(anchor.product_id);
    if (!anchor.handle || addonFetched[id]) return;
    addonFetched[id] = true;
    fetch('/products/' + encodeURIComponent(anchor.handle) + '?view=cart-addons')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) {
        var arr = null;
        try { arr = JSON.parse(t); } catch (e) { arr = null; }
        if (!arr || !arr.filter(Boolean).length) return;
        CFG.addonRail = CFG.addonRail || {};
        CFG.addonRail[id] = arr;
        render();
      })
      .catch(function () {});
  }

  function upsellHTML() {
    var reco = recommendedFor();
    var source = reco || (CFG.upsell || []).filter(Boolean);
    var list = source.filter(function (u) {
      return !cart.items.some(function (i) { return i.product_id === u.productId; });
    });
    if (!CFG.upsellEnabled || !list.length) return '';

    var cards = list.slice(0, 12).map(function (u) {
      /* When the cart holds a pack, the ladder delta IS the price this item
         costs — presented like any other price (MRP struck, price, % off)
         rather than as a "+N" adjustment.

         But the ladder is only worth taking when the STEP is cheaper than
         buying the item outright. A Rs.289 lip balm added to a pack-of-2
         steps the ladder Rs.699 -> Rs.999, i.e. Rs.300 — quoting that would
         charge Rs.11 MORE than the item costs on its own. So when the step is
         the worse deal we show the real price and add the item UNTAGGED, so
         it sits outside the pack at its own price. */
      var delta = ladderDelta(u.price, u.units || 1);
      var useLadder = (delta !== null && delta < u.price);
      var eff = useLadder ? delta : u.price;
      var mrpRef = (u.mrp && u.mrp > u.price) ? u.mrp : u.price;
      var off = pctOff(mrpRef, eff);
      return '<div class="ecart-ucard">' +
        '<div class="ecart-ucard-l">' +
          '<img src="' + esc(u.image) + '" alt="' + esc(u.title) + '" loading="lazy" width="52" height="52">' +
          (off ? '<span class="ecart-ubadge">' + off + '% off</span>' : '') +
        '</div>' +
        '<div class="ecart-ucard-r">' +
          '<a class="ecart-uname" href="' + esc(u.url) + '" title="' + esc(u.fullTitle || u.title) + '">' +
            esc(u.title) + '</a>' +
          '<p class="ecart-uprice">' +
            (mrpRef > eff ? '<s>' + money(mrpRef) + '</s>' : '') +
            '<span>' + (eff === 0 ? 'FREE' : money(eff)) + '</span>' +
          '</p>' +
          (useLadder ? '<span class="ecart-upack">pack price</span>' : '') +
          '<button type="button" class="ecart-add" data-ec-add="' + u.variantId + '"' +
          ' data-ec-add-units="' + (u.units || 1) + '"' +
          ' data-ec-add-join="' + (useLadder ? '1' : '0') + '"' +
          /* Record the exact figure on the card so the line can show the very
             number the shopper was quoted, rather than a reconstruction of it. */
          (useLadder ? ' data-ec-add-quoted="' + eff + '"' : '') +
          '>+ Add</button>' +
        '</div>' +
      '</div>';
    }).join('');

    return '<div class="ecart-upsell">' +
      '<span class="ecart-upsell-t">' + esc(CFG.upsellTitle || 'Boost your results') + '</span>' +
      '<div class="ecart-scroll">' + cards + '</div>' +
    '</div>';
  }

  /* Footer is server-rendered; we only repaint its numbers so the
     .gokwik-checkout button element itself is never torn out. */
  function paintFooter(sub, mrp) {
    var saved = mrp - sub;
    var off = pctOff(mrp, sub);

    els.ribbon.classList.toggle('is-hidden', !(CFG.ribbonEnabled && saved > 0));
    if (saved > 0) els.ribbonText.textContent = money(saved) + ' Saved so far!';

    if (els.discounts) {
      var ds = cartDiscounts();
      /* When the saving is already spread across the lines, repeating it as a
         numeric row makes the arithmetic look wrong: the lines add to the
         total, and then a minus figure appears under them. Name the discount
         without the amount in that case. */
      var onLines = !!allocMap;
      els.discounts.innerHTML = ds.map(function (d) {
        return '<span class="ecart-cartdisc' + (onLines ? ' is-applied' : '') + '">' +
          '<span>' + esc(d.title) + '</span>' +
          (onLines ? '<b>applied</b>' : '<b>\u2212' + money(d.amount) + '</b>') +
          '</span>';
      }).join('');
      els.discounts.hidden = !ds.length;
    }

    els.totalStrike.textContent = saved > 0 ? money(mrp) : '';
    els.totalStrike.hidden = saved <= 0;
    els.totalNow.textContent = money(sub);
    els.totalOff.textContent = off ? '(' + off + '% OFF)' : '';
    els.totalOff.hidden = !off;

    if (els.coSub) {
      var min = (CFG.prepaidMin || 0) * 100;
      var rate = CFG.prepaidRate || 0;
      if (!rate) {
        els.coSub.hidden = true;
      } else {
        els.coSub.hidden = false;
        els.coSub.textContent = sub >= min
          ? 'Extra ' + rate + '% off on prepaid'
          : 'Extra ' + rate + '% off prepaid above ' + moneyFlat(min);
      }
    }
  }

  function paintBars() {
    var fills = els.body.querySelectorAll('.ecart-fill');
    for (var i = 0; i < fills.length; i++) fills[i].style.width = fills[i].getAttribute('data-w') + '%';
    if (tierPage > maxTierPage()) tierPage = maxTierPage();
    var track = els.body.querySelector('[data-ec-track]');
    if (track) track.style.transform = 'translateX(calc(' + (-tierPage * 50) + '% - ' + (tierPage * 4) + 'px))';
  }

  /* This theme renders the bubble as a BARE number — <div class="cart-count-bubble">1</div>
     — with no inner span, unlike stock Dawn. Only writing to
     `span[aria-hidden]` meant the number never updated at all and simply kept
     whatever Liquid printed at page load. Handle both shapes. */
  function syncBubbles(n) {
    var bubbles = document.querySelectorAll('.cart-count-bubble');
    for (var i = 0; i < bubbles.length; i++) {
      var b = bubbles[i];
      b.style.display = n > 0 ? '' : 'none';

      var inner = b.querySelector('span[aria-hidden="true"]');
      if (inner) {
        inner.textContent = n;
      } else if (!b.children.length) {
        b.textContent = n > 0 ? String(n) : '';
      }

      var hidden = b.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = n + (n === 1 ? ' item' : ' items');
      b.setAttribute('aria-label', n + (n === 1 ? ' item' : ' items') + ' in cart');
    }
  }

  /* ---------- mutations ---------- */

  function setBusy(v) {
    busy = v;
    els.panel.classList.toggle('is-busy', v);
    /* Mutations are the slow half (add -> convert -> gift sync), so they drive
       the status strip too. Reference-counted in workStart/workEnd: a helper
       that ends in refresh() must not have the inner refresh clear its strip. */
    if (v) workStart(); else workEnd();
  }

  /* Tier-unlock detection lives here, not in the mutation helpers, because a
     cart can change without going through them at all — Dawn's Add to Cart
     calls renderContents() -> refresh(), which is the single most common way
     a shopper crosses a threshold. Hanging this off the helpers meant the
     celebration almost never fired. */
  function checkTierUnlocks() {
    var sub = eligibleSubtotal();
    tiers().forEach(function (t, i) {
      var target = t.amount * 100;
      if (sub >= target && !unlockedSeen[i]) {
        unlockedSeen[i] = true;
        tierPage = Math.min(i, maxTierPage());
        paintBars();
        confetti();
        toast('\uD83C\uDF81 ' + t.name + ' unlocked \u2014 pick your free gift');
      }
      if (sub < target) delete unlockedSeen[i];
    });
  }

  function change(key, qty) {
    if (busy) return;
    setBusy(true);
    postJSON('/cart/change.js', { id: key, quantity: qty })
      .then(function () { return refresh(false); })
      .catch(function (e) { toast(e.message || 'Could not update the cart'); })
      .then(function () { setBusy(false); });
  }

  /* Joins an EXISTING pack, never starts one: we only stamp _byob when the
     cart already contains pack lines, i.e. the shopper arrived from the BYOB
     page or the PDP frequently-bought-together. A plain shopper adding two
     things from the cart must not silently trigger pack pricing. */
  function addVariant(variantId, qty, units, joinPack, quoted) {
    if (busy) return Promise.resolve();
    setBusy(true);
    var item = { id: Number(variantId), quantity: qty || 1 };
    if (joinPack !== false && packState()) {
      item.properties = {
        _byob: PACK_TYPE,
        _byob_v: String(variantId),
        _byob_units: String(units || 1)
      };
      /* The ladder step quoted on the rail is measured against the cart's
         ACTUAL total at this moment, which is not reconstructible afterwards:
         once the pack forms, the whole cart reprices and the earlier total is
         gone. So record the promise here and honour it in packAllocation().
         Underscore-prefixed, so it stays hidden from the customer everywhere. */
      if (quoted != null && quoted !== '' && !isNaN(Number(quoted))) {
        item.properties._byob_quoted = String(Math.max(0, Math.round(Number(quoted))));
      }
    }
    return postJSON('/cart/add.js', { items: [item] })
      .then(function () {
        /* Fold the rest of the cart in too, otherwise the new line is a pack
           of one and the ladder price we just quoted would not materialise. */
        return (item.properties) ? convertCartToPack() : null;
      })
      .then(function () { return refresh(false); })
      .catch(function (e) { toast(e.message || 'Could not add that item'); })
      .then(function () { setBusy(false); });
  }

  /* Shopify has no "swap variant" call — remove then re-add, which is what
     the GoKwik drawer does too. Line properties are not carried over. */
  /* Re-find a line by what it IS (variant + properties) rather than by the key
     we saw earlier, because keys rotate on every cart mutation. */
  function removeLineLike(orig) {
    var sig = JSON.stringify(orig.properties || {});
    return json('/cart.js').then(function (fresh) {
      var target = null;
      fresh.items.forEach(function (i) {
        if (i.variant_id === orig.variant_id && JSON.stringify(i.properties || {}) === sig) target = i;
      });
      if (!target) return null;
      return postJSON('/cart/change.js', { id: target.key, quantity: 0 });
    });
  }

  function switchVariant(key, newVariantId, qty) {
    if (busy) return;
    var vid = Number(newVariantId);
    if (!vid) return;                       /* never act on an empty select */

    var line = null;
    cart.items.forEach(function (i) { if (i.key === key) line = i; });
    if (!line || vid === line.variant_id) return;

    var wasPack = isByob(line);
    var newTitle = '';
    var prod = productCache[line.handle];
    var valid = false;
    if (prod) prod.variants.forEach(function (v) {
      if (v.id === vid) { valid = true; newTitle = v.title; }
    });
    if (!valid) return;                     /* variant is not on this product */

    setBusy(true);
    var item = { id: vid, quantity: qty };
    if (wasPack) {
      /* Carry the pack across the swap, re-pointed at the new variant.
         _byob_units is recomputed too, so swapping UP to a bigger pack raises
         the unit count and the ladder tier rather than riding the old price —
         which is the free upgrade the _byob_v guard exists to stop. */
      item.properties = {
        _byob: line.properties._byob || PACK_TYPE,
        _byob_v: String(vid),
        _byob_units: String(unitsForVariant(vid, newTitle))
      };
    }

    /* ADD FIRST, then remove the old line. The reverse order loses the item
       outright if the add fails (sold out, bad id) — a transient duplicate is
       far less damaging, and the refresh below reconciles it.

       The old line must be re-found after the add: Shopify rotates line keys
       on every mutation, so `key` is stale by then and change.js 400s. */
    postJSON('/cart/add.js', { items: [item] })
      .then(function () { return removeLineLike(line); })
      .then(function () { return refresh(false); })
      .catch(function (e) {
        toast(e.message || 'That pack size is unavailable');
        return refresh(false);
      })
      .then(function () { setBusy(false); });
  }

  /* ---------- events ---------- */

  function wire() {
    var b = els.body;

    els.sheet.querySelectorAll('[data-ec-picker-close]').forEach(function (el) {
      el.onclick = function () { pickerFor = null; render(); };
    });
    els.sheet.querySelectorAll('[data-ec-pick]').forEach(function (el) {
      el.onclick = function () {
        chooseGift(Number(el.getAttribute('data-ec-pick-tier')), el.getAttribute('data-ec-pick'));
      };
    });

    b.querySelectorAll('[data-ec-inc]').forEach(function (el) {
      el.onclick = function () { change(el.getAttribute('data-ec-inc'), qtyOf(el.getAttribute('data-ec-inc')) + 1); };
    });
    b.querySelectorAll('[data-ec-dec]').forEach(function (el) {
      el.onclick = function () {
        var q = qtyOf(el.getAttribute('data-ec-dec'));
        if (q > 1) change(el.getAttribute('data-ec-dec'), q - 1);
      };
    });
    b.querySelectorAll('[data-ec-qty]').forEach(function (el) {
      el.onchange = function () {
        var v = Math.max(1, Math.floor(Number(el.value) || 1));
        change(el.getAttribute('data-ec-qty'), v);
      };
    });
    b.querySelectorAll('[data-ec-del]').forEach(function (el) {
      el.onclick = function () { change(el.getAttribute('data-ec-del'), 0); };
    });
    b.querySelectorAll('[data-ec-variant]').forEach(function (el) {
      el.onchange = function () {
        var key = el.getAttribute('data-ec-variant');
        switchVariant(key, el.value, qtyOf(key));
      };
    });
    b.querySelectorAll('[data-ec-add]').forEach(function (el) {
      el.onclick = function () {
        el.disabled = true;
        addVariant(el.getAttribute('data-ec-add'), 1,
                   Number(el.getAttribute('data-ec-add-units')) || 1,
                   el.getAttribute('data-ec-add-join') !== '0',
                   el.getAttribute('data-ec-add-quoted'));
      };
    });
    b.querySelectorAll('[data-ec-picker]').forEach(function (el) {
      el.onclick = function () { pickerFor = Number(el.getAttribute('data-ec-picker')); render(); };
    });
    b.querySelectorAll('[data-ec-tier]').forEach(function (el) {
      el.onclick = function () {
        var T = tiers();
        tierPage = Math.max(0, Math.min(maxTierPage(), tierPage + Number(el.getAttribute('data-ec-tier'))));
        render();
      };
    });
  }

  function qtyOf(key) {
    if (!cart) return 1;
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === key) return cart.items[i].quantity;
    return 1;
  }

  /* ---------- open / close ---------- */

  function openCart() {
    els.overlay.classList.add('is-open');
    els.panel.classList.add('is-open');
    document.body.classList.add('ecart-open');
    els.panel.setAttribute('aria-hidden', 'false');
    if (els.close) els.close.focus({ preventScroll: true });
  }

  function closeCart() {
    els.overlay.classList.remove('is-open');
    els.panel.classList.remove('is-open');
    document.body.classList.remove('ecart-open');
    els.panel.setAttribute('aria-hidden', 'true');
  }

  /* ---------- toast ---------- */

  var toastTimer;
  function toast(msg) {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { els.toast.classList.remove('is-on'); }, 2600);
  }

  /* ---------- confetti ---------- */

  var bits = [], raf = null, ctx = null;

  function confetti() {
    if (!els.canvas) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    ctx = ctx || els.canvas.getContext('2d');
    var r = els.canvas.getBoundingClientRect();
    if (!r.width) return;
    var dpr = window.devicePixelRatio || 1;
    els.canvas.width = r.width * dpr;
    els.canvas.height = r.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var cols = ['#00CC66', '#3CB9F7', '#15B79E', '#FFC93C', '#FF6B6B', '#B266FF'];
    /* Small, light pieces like GoKwik's — the earlier oversized version read
       as chunky. Density comes from count and spread, not from piece size. */
    for (var i = 0; i < 110; i++) {
      bits.push({
        x: r.width * 0.5 + (Math.random() - .5) * r.width * 0.8,
        y: r.height * 0.34,
        vx: (Math.random() - .5) * 7,
        vy: -Math.random() * 7 - 3,
        w: 4 + Math.random() * 3,
        h: 6 + Math.random() * 5,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - .5) * .32,
        c: cols[(Math.random() * cols.length) | 0],
        life: 1
      });
    }
    if (!raf) raf = requestAnimationFrame(step);
  }

  function step() {
    var r = els.canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, r.width, r.height);
    bits = bits.filter(function (b) { return b.life > 0; });
    bits.forEach(function (b) {
      b.vy += 0.24; b.vx *= 0.995; b.x += b.vx; b.y += b.vy; b.rot += b.vr;
      if (b.y > r.height * 0.78) b.life -= 0.025;
      ctx.save();
      ctx.globalAlpha = Math.max(0, b.life);
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.fillStyle = b.c;
      ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
      ctx.restore();
    });
    if (bits.length) { raf = requestAnimationFrame(step); }
    else { raf = null; ctx.clearRect(0, 0, r.width, r.height); }
  }

  /* ---------- prefilled cart from the URL ---------- */

  /* A link can build the cart and open this drawer over whatever page it points
     at:  ?ids=<variantId>[,<variantId>...]&qtys=<n>[,<n>...]
     That lets a WhatsApp reply land someone on the real PDP with their cart
     already made, instead of on /pages/wa-checkout -- which showed none of the
     gift ladder, none of the upsell rail and none of the prepaid nudge.

     Reads `ids` ONLY, never a bare `id`: this script runs on every page, and a
     stray ?id= from some other app must never be read as "wipe the cart".

     Lines are added ONE AT A TIME on purpose. /cart/add.js is atomic, so a
     single dead variant 422s the whole batch; /pages/wa-checkout cleared the
     cart first and swallowed that error, which left the shopper staring at an
     empty cart under a live "Checkout Now" button (reproduced 2026-09-14 with
     an unpublished variant). Adding individually costs a dead id its own line
     and nothing else -- and render() hides the footer at zero items, so the
     dead-button state cannot come back here.

     Returns true when it has taken over the boot refresh. */
  function prefillFromUrl() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return false; }

    var ids = (q.get('ids') || '').split(',')
      .map(function (s) { return parseInt(s.trim(), 10); })
      .filter(function (n) { return n > 0; });
    if (!ids.length) return false;

    var qtyRaw = (q.get('qtys') || q.get('qty') || '').split(',');
    var qtys = ids.map(function (_, i) {
      var n = parseInt((qtyRaw[i] || '').trim(), 10);
      return n > 0 ? n : 1;
    });

    /* Strip the params BEFORE any async work: a reload, a back-nav or a double
       load must not clear and rebuild the cart a second time. */
    try {
      ['ids', 'qtys', 'qty', 'loaded'].forEach(function (k) { q.delete(k); });
      var rest = q.toString();
      history.replaceState({}, '',
        window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
    } catch (e) {}

    openCart();
    setBusy(true);

    var failed = 0;
    fetch('/cart/clear.js', { method: 'POST' }).then(function () {
      /* Sequential, not Promise.all -- Shopify serialises cart writes and a
         parallel burst can silently drop lines. */
      return ids.reduce(function (chain, id, i) {
        return chain.then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: id, quantity: qtys[i] }] })
          }).then(function (r) { if (!r.ok) failed++; }, function () { failed++; });
        });
      }, Promise.resolve());
    }).catch(function () {
      failed = ids.length;
    }).then(function () {
      setBusy(false);
      return refresh(true);
    }).then(function () {
      if (!failed) return;
      toast(failed >= ids.length
        ? 'Sorry, that link is no longer available'
        : failed + (failed === 1 ? ' item is' : ' items are') + ' no longer available');
    });

    return true;
  }

  /* ---------- boot ---------- */

  function init() {
    var root = document.getElementById('ElementCart');
    if (!root) return;

    els.root = root;
    els.panel = root.querySelector('.ecart-panel');
    els.overlay = root.querySelector('.ecart-overlay');
    els.title = root.querySelector('[data-ec-title]');
    els.body = root.querySelector('[data-ec-body]');
    els.footWrap = root.querySelector('[data-ec-foot]');
    els.ribbon = root.querySelector('.ecart-ribbon');
    els.ribbonText = root.querySelector('[data-ec-ribbon-text]');
    els.totalStrike = root.querySelector('[data-ec-total-strike]');
    els.totalNow = root.querySelector('[data-ec-total-now]');
    els.totalOff = root.querySelector('[data-ec-total-off]');
    els.coSub = root.querySelector('[data-ec-checkout-sub]');
    els.discounts = root.querySelector('[data-ec-discounts]');
    els.close = root.querySelector('.ecart-close');
    els.toast = root.querySelector('.ecart-toast');
    els.sheet = root.querySelector('[data-ec-sheet]');
    els.canvas = root.querySelector('.ecart-confetti');

    syncLadderFromBuilder();
    els.close.addEventListener('click', closeCart);
    els.overlay.addEventListener('click', closeCart);
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !els.panel.classList.contains('is-open')) return;
      if (pickerFor !== null) { pickerFor = null; render(); return; }
      closeCart();
    });

    /* Delegated, capturing — beats the theme's own /cart links and covers
       any icon rendered after load. No observer needed. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest(
        '#cart-icon-bubble, .header__icon--cart, a[href="/cart"], a[href$="' + window.location.origin + '/cart"], [data-ec-open]'
      );
      if (!t) return;
      if (t.closest('#ElementCart')) return;
      e.preventDefault();
      e.stopPropagation();
      openCart();
      refresh(false);
    }, true);

    /* GoKwik owns this button: the inline onclick calls its global
       onCheckoutClick, and snippets/gokwik.liquid re-enables it when
       `gokwikLoaded` fires. That means a GoKwik outage would leave the only
       checkout button permanently disabled, so after a grace period we take
       it over and fall back to Shopify checkout. */
    var co = root.querySelector('.gokwik-checkout button');
    if (co) {
      setTimeout(function () {
        if (typeof window.onCheckoutClick === 'function') return;
        console.warn('[element-cart] GoKwik checkout unavailable, falling back to /checkout');
        co.removeAttribute('onclick');
        co.disabled = false;
        co.classList.remove('disabled');
        co.addEventListener('click', function () { window.location.href = '/checkout'; });
      }, 6000);
    }

    /* THE HANDOFF GUARD. Once GoKwik takes over, nothing of ours runs again —
       it reads the Shopify cart and builds the order from it. So the cart must
       be correct at the instant of the tap, and it very often was not:

         - The gift is a real Rs.0 cart LINE, and the only thing that revokes an
           unearned one is syncGifts().
         - Dropping a line runs change.js -> cart.js -> gift pool -> update.js,
           four sequential fetches, seconds on a phone.
         - This button sits outside .ecart-body, so it stayed clickable for that
           whole window.

       Tap it mid-flight and GoKwik snapshots a cart still holding a gift the
       cart no longer earns. That is order TE25-45399: a Rs.899 cart that left
       with a Tier 2 gift, because the Rs.379 line that had earned it was
       removed a moment before checkout.

       So: swallow the tap, wait for any in-flight mutation to settle, run one
       more authoritative refresh (which re-runs syncGifts against a fresh
       /cart.js), and only then hand over. The shopper sees no total change —
       the gift is Rs.0, so revoking it moves no money. */
    var coWrap = root.querySelector('.gokwik-checkout');
    if (coWrap) {
      var checkoutPending = false;

      /* Never wait forever: a wedged `busy` must not brick checkout. */
      var settle = function (deadline) {
        return new Promise(function (done) {
          (function tick() {
            if (!busy || Date.now() > deadline) return done();
            setTimeout(tick, 60);
          })();
        });
      };

      coWrap.addEventListener('click', function (e) {
        var btn = coWrap.querySelector('button');
        if (!btn || btn.disabled) return;

        /* Capture on the WRAPPER, not the button. An ancestor's capturing
           listener is guaranteed by spec to run before the target's own
           handlers, so GoKwik's parse-time inline onclick cannot beat us to it.
           A capture listener on the BUTTON also happens to win in current
           Chrome (measured), but at-target ordering between an inline handler
           and a capture listener is an implementation detail — and this ships
           to Android Instagram WebViews of every vintage. Don't rely on it. */
        e.preventDefault();
        e.stopPropagation();
        if (checkoutPending) return;

        checkoutPending = true;
        coWrap.classList.add('is-verifying');

        /* HARD CEILING on the whole re-check, not just the `busy` wait.
           refresh() has no timeout of its own and its chain keeps growing —
           it now runs resolveGiftPool -> repairPackStamps (+2 fetches when a
           stale stamp is found) -> syncGifts -> a re-read, each a round trip
           on a phone. Awaiting that unbounded means one hung request leaves a
           shopper tapping a dead Checkout button.

           So: whichever finishes first wins, and we hand off regardless. The
           re-check is best-effort by design — an unearned gift costs us one
           Rs.0 SKU, a checkout that never opens costs the whole order. */
        var deadline = Date.now() + 5000;
        var timeout = new Promise(function (go) { setTimeout(go, 6000); });

        Promise.race([
          settle(deadline).then(function () { return refresh(false); }),
          timeout,
        ])
          .catch(function (err) {
            /* A failed re-check must not trap the shopper in the cart either. */
            console.warn('[element-cart] pre-checkout re-check failed', err);
            return null;
          })
          .then(function () {
            coWrap.classList.remove('is-verifying');
            checkoutPending = false;
            if (typeof window.onCheckoutClick === 'function') window.onCheckoutClick(btn);
            else window.location.href = '/checkout';
          });
      }, true);
    }

    /* The PDP sticky bar defines its own add-to-cart that hard-redirects to
       /cart. Its buttons use inline onclick, which resolves the name off
       window at click time, so reassigning it here makes them open the drawer
       instead. Guarded so we only touch it where it exists.

       CRITICAL: when the Frequently Bought Together builder has add-ons
       selected it must post the BUNDLE (every line stamped _byob/_byob_v/
       _byob_units), not the product form. Posting the form drops the pack
       entirely — one untagged line, no ladder discount — which is exactly
       what a shopper who built a pack then tapped the sticky bar would get. */
    var bundleItems = null;
    document.addEventListener('byob:change', function (e) {
      var d = e && e.detail;
      bundleItems = (d && d.addons > 0 && d.items && d.items.length) ? d.items : null;
    });

    if (typeof window.stickyAddToCartFromForm === 'function') {
      window.stickyAddToCartFromForm = function () {
        var form = document.querySelector('form[action*="/cart/add"]');
        var btns = document.querySelectorAll('.sticky-add-to-cart-btn, .sticky-add-to-cart-btn-mobile');
        var reset = function () {
          Array.prototype.forEach.call(btns, function (b) { b.disabled = false; b.textContent = 'Add to Cart'; });
        };

        /* Ask the builder to re-broadcast NOW — byob:change fires synchronously
           from its render(), so bundleItems is fresh by the next line even if
           the shopper taps before any other handshake has happened. */
        try { document.dispatchEvent(new CustomEvent('byob:request')); } catch (e) {}

        if (!form && !bundleItems) return;
        Array.prototype.forEach.call(btns, function (b) { b.disabled = true; b.textContent = 'Adding...'; });

        /* The sticky bar never submits the form, so the submit hook above cannot
           see it — bracket it here or its add is invisible to the status clock. */
        workStart();
        var release = function () { workEnd(); };

        var req = bundleItems
          ? postJSON('/cart/add.js', { items: bundleItems })
          : fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
              .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); });

        req.then(function () { return refresh(true); })
           .then(release, release)
           .catch(function () { window.location.href = '/cart'; })
           .then(reset);
      };
    }

    /* sections/byob-builder.liquid and sections/bundle-builder.liquid both
       call window.openGokwikSideCart() after adding a pack. That global came
       from the GoKwik app embed, which this theme disables — without a shim
       the BYOB Add to Cart throws and the cart never opens. */
    if (typeof window.openGokwikSideCart !== 'function') {
      window.openGokwikSideCart = function () { openCart(); refresh(false); };
    }
    if (typeof window.closeGokwikSideCart !== 'function') {
      window.closeGokwikSideCart = closeCart;
    }

    window.ElementCart = {
      open: function () { openCart(); refresh(false); },
      close: closeCart,
      refresh: refresh,
      addVariant: addVariant,
      prefillFromUrl: prefillFromUrl
    };

    /* A ?ids= link does its own opening refresh, so the plain boot refresh
       would only race it for the same /cart.js. */
    if (!prefillFromUrl()) refresh(false);
  }

  /* WATCH the add we do not own, so a failure is caught in ~700ms rather than
     at the 5s failsafe. Dawn performs the POST itself and, on a 422 (sold out,
     inventory cap), never calls renderContents — so without this the optimistic
     line stands until the failsafe fires. Measured: the phantom line survived a
     full 5s, which is far too long to show someone an item they are not getting.

     This only OBSERVES: the original promise is returned untouched and the body
     is never read, so it cannot interfere with product-form.js consuming it.
     The failsafe stays as the backstop for an add that never settles at all. */
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var url = String((input && input.url) || input || '');
    var p = nativeFetch.apply(this, arguments);
    if (url.indexOf('/cart/add') !== -1) {
      p.then(function (r) {
        if (pendingAdd && r && !r.ok) { releaseAdd(); refresh(false); }
      }, function () {
        if (pendingAdd) { releaseAdd(); refresh(false); }
      });
    }
    return p;
  };

  /* Start the status clock at the click, not at the response. A sold-out or
     validation failure never reaches renderContents, so a failsafe releases it;
     it sits under STATUS_MAX so the overlay can never outlive its own backstop.
     (A stuck overlay there would be invisible anyway — the panel never opened.) */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || typeof f.matches !== 'function') return;
    if (!f.matches('form[action*="/cart/add"]')) return;
    if (pendingAdd) return;                  /* double-tap must not double-count */
    pendingAdd = true;
    var vidEl = f.querySelector('[name="id"]');
    var qtyEl = f.querySelector('[name="quantity"]');
    if (vidEl) {
      optimisticAdd(Number(vidEl.value), Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1));
    }
    workStart();
    clearTimeout(addFailSafe);
    /* A sold-out add never calls renderContents, so reconcile against the
       server rather than leaving the optimistic line standing. */
    addFailSafe = setTimeout(function () { releaseAdd(); refresh(false); }, 5000);
  }, true);

  /* Dawn's product-form.js redirects to /cart unless it finds a <cart-drawer>.
     Implementing the three methods it calls turns every Add to Cart on the
     site into "open the drawer" without touching product-form.js. */
  if (!window.customElements.get('cart-drawer')) {
    window.customElements.define('cart-drawer', class extends HTMLElement {
      getSectionsToRender() { return []; }
      setActiveElement(el) { this.activeElement = el; }
      renderContents() {
        if (window.ElementCart) {
          /* refresh() resolves after its OWN workEnd, so releasing here lands
             the counter on zero and the overlay clears with the finished cart. */
          Promise.resolve(window.ElementCart.refresh(true)).then(releaseAdd, releaseAdd);
        } else { window.location.href = '/cart'; }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
